'use client';

import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Input,
  Link,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { ExternalLink, Plus, Trash2, Youtube } from 'lucide-react';
import { useState } from 'react';
import { LoadingState } from '@/components/shared/ui';
import { AttuneSelect } from '@/components/shared/ui/AttuneSelect';
import { useYouTubeApi, useYouTubeStatus } from '@/components/youtube/useYouTube';
import { useProject } from '@/contexts/ProjectContext';
import { DEFAULT_CONFIG } from '@/lib/youtube/config';
import type { YouTubeChannel, YouTubeConfig } from '@/lib/youtube/types';
import { IntegrationCard } from './IntegrationCardShell';

export function YouTubeIntegration({ onStatusChanged }: { onStatusChanged?: () => void }) {
  const { currentProject } = useProject();
  return (
    <YouTubeConnectionForm key={currentProject?.id || 'none'} onStatusChanged={onStatusChanged} />
  );
}

function YouTubeConnectionForm({ onStatusChanged }: { onStatusChanged?: () => void }) {
  const { request } = useYouTubeApi();
  const { data: status, error: statusError, loading, reload } = useYouTubeStatus();
  const [draftConfig, setConfig] = useState<YouTubeConfig | null>(null);
  const config = draftConfig || status?.config || DEFAULT_CONFIG;
  const initialized = Boolean(status);
  const [url, setUrl] = useState('');
  const [role, setRole] = useState<YouTubeChannel['role']>('owned');
  const [draftTopics, setTopics] = useState<string | null>(null);
  const topics = draftTopics ?? status?.config.topics.join('\n') ?? '';
  const [busy, setBusy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const canManage = status?.canManage || false;

  async function resolveChannel() {
    setBusy('resolve');
    setError(null);
    setMessage(null);
    try {
      const result = await request<{ channel: YouTubeChannel }>('/api/integrations/youtube', {
        body: { action: 'resolve', url, role },
      });
      if (config.channels.some(channel => channel.id === result.channel.id))
        throw new Error('That channel is already in your list.');
      setConfig(previous => ({ ...(previous || config), channels: [...(previous || config).channels, result.channel] }));
      setUrl('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy('');
    }
  }

  async function save(action: 'connect' | 'disconnect') {
    setBusy(action);
    setError(null);
    setMessage(null);
    try {
      await request('/api/integrations/youtube', {
        body: {
          action,
          config: {
            ...config,
            topics: topics
              .split('\n')
              .map(topic => topic.trim())
              .filter(Boolean),
          },
        },
      });
      setMessage(
        action === 'disconnect'
          ? 'Disconnected. Your collected history is still available.'
          : 'Saved. Collection has started and will update daily.',
      );
      reload();
      onStatusChanged?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy('');
    }
  }

  return (
    <IntegrationCard
      icon={<Youtube size={20} />}
      title="YouTube"
      description="Follow your videos, Shorts, competitors, and AI citations."
      statusLabel={status?.connected ? 'Connected · Daily updates' : 'Not connected'}
      statusTone={status?.connected ? 'connected' : 'idle'}
    >
      {loading && !initialized ? <LoadingState message="Loading YouTube connection…" /> : null}
      {(error || statusError) && (
        <Text role="alert" color="status.danger.fg" fontSize="sm">
          {error || statusError}
        </Text>
      )}
      {message && (
        <Text role="status" color="status.success.fg" fontSize="sm">
          {message}
        </Text>
      )}
      {status && !status.available && (
        <Text color="status.warning.fg" fontSize="sm">
          YouTube collection needs to be enabled by your Attune administrator.
        </Text>
      )}
      {initialized && (
        <VStack align="stretch" gap="5">
          <Box>
            <Text fontWeight="600" fontSize="sm" mb="1">
              Channels
            </Text>
            <Text color="ui.text.secondary" fontSize="sm" mb="3">
              Add your company’s channels and the competitors you want to follow.
            </Text>
            <VStack align="stretch" gap="2">
              {config.channels.map(channel => (
                <HStack
                  key={channel.id}
                  justify="space-between"
                  gap="3"
                  py="2"
                  borderBottomWidth="1px"
                  borderColor="ui.border.subtle"
                >
                  <HStack minW="0" gap="2">
                    {channel.thumbnail && (
                      <Image src={channel.thumbnail} alt="" boxSize="8" borderRadius="full" />
                    )}
                    <Box minW="0">
                      <Text fontSize="sm" fontWeight="600" truncate>
                        {channel.title}
                      </Text>
                      <Link
                        href={channel.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontSize="xs"
                        color="ui.text.secondary"
                      >
                        View channel <ExternalLink size={11} />
                      </Link>
                    </Box>
                  </HStack>
                  <HStack>
                    <Badge size="sm" variant="subtle">
                      {channel.role === 'owned' ? 'Ours' : 'Competitor'}
                    </Badge>
                    {canManage && (
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label={`Remove ${channel.title}`}
                        disabled={Boolean(busy)}
                        onClick={() =>
                          setConfig(previous => ({
                            ...(previous || config),
                            channels: (previous || config).channels.filter(item => item.id !== channel.id),
                          }))
                        }
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    )}
                  </HStack>
                </HStack>
              ))}
            </VStack>
            {canManage && (
              <VStack align="stretch" gap="2" mt="3">
                <label htmlFor="youtube-channel-url">
                  <Text fontSize="xs" color="ui.text.secondary">
                    Channel URL or @handle
                  </Text>
                </label>
                <Input
                  id="youtube-channel-url"
                  value={url}
                  onChange={event => setUrl(event.target.value)}
                  placeholder="https://www.youtube.com/@yourcompany"
                  disabled={Boolean(busy) || !status?.available}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && url.trim()) {
                      event.preventDefault();
                      void resolveChannel();
                    }
                  }}
                />
                <HStack justify="space-between" gap="3">
                  <AttuneSelect
                    ariaLabel="Channel ownership"
                    value={role}
                    options={[
                      { value: 'owned', label: 'Our channel' },
                      { value: 'competitor', label: 'Competitor' },
                    ]}
                    onValueChange={value => setRole(value as YouTubeChannel['role'])}
                    size="sm"
                    maxW="180px"
                    disabled={Boolean(busy)}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void resolveChannel()}
                    loading={busy === 'resolve'}
                    disabled={
                      !url.trim() ||
                      !status?.available ||
                      Boolean(busy) ||
                      config.channels.length >= 10
                    }
                  >
                    <Plus size={14} /> Add channel
                  </Button>
                </HStack>
              </VStack>
            )}
          </Box>
          <Box>
            <label htmlFor="youtube-topics">
              <Text fontSize="sm" fontWeight="600" mb="1">
                Topics to follow
              </Text>
            </label>
            <Text color="ui.text.secondary" fontSize="sm" mb="2">
              Your brand, executives, campaigns, or industry questions. One search per line, up to
              five.
            </Text>
            <Textarea
              id="youtube-topics"
              value={topics}
              onChange={event => setTopics(event.target.value)}
              rows={3}
              placeholder={'Company name\nIndustry topic'}
              disabled={!canManage || Boolean(busy)}
            />
          </Box>
          <HStack gap="4" align="flex-start">
            <Box flex="1">
              <Text fontSize="xs" mb="1">
                Search market
              </Text>
              <AttuneSelect
                ariaLabel="YouTube search market"
                value={config.country}
                disabled={!canManage || Boolean(busy)}
                onValueChange={country => setConfig(previous => ({ ...(previous || config), country }))}
                options={[
                  ['US', 'United States'],
                  ['GB', 'United Kingdom'],
                  ['CA', 'Canada'],
                  ['AU', 'Australia'],
                  ['DE', 'Germany'],
                  ['FR', 'France'],
                  ['IN', 'India'],
                  ['JP', 'Japan'],
                  ['BR', 'Brazil'],
                ].map(([value, label]) => ({ value, label }))}
              />
            </Box>
            <Box flex="1">
              <Text fontSize="xs" mb="1">
                Language
              </Text>
              <AttuneSelect
                ariaLabel="YouTube search language"
                value={config.language}
                disabled={!canManage || Boolean(busy)}
                onValueChange={language => setConfig(previous => ({ ...(previous || config), language }))}
                options={[
                  ['en', 'English'],
                  ['es', 'Spanish'],
                  ['fr', 'French'],
                  ['de', 'German'],
                  ['ja', 'Japanese'],
                  ['pt', 'Portuguese'],
                  ['hi', 'Hindi'],
                ].map(([value, label]) => ({ value, label }))}
              />
            </Box>
          </HStack>
          <Text fontSize="xs" color="ui.text.tertiary">
            {status?.requestsToday || 0} collection requests today · daily limit{' '}
            {status?.requestLimit || 200} per project.
          </Text>
          <Text fontSize="xs" color="ui.text.tertiary">
            Updates automatically every day. Collects up to 200 videos and 200 Shorts per channel,
            plus two pages per topic search. Growth becomes available after comparable observations.
          </Text>
          <HStack justify="space-between" flexWrap="wrap" gap="2">
            {status?.configured && (
              <Link href="/app/youtube" fontSize="sm" fontWeight="600">
                Open YouTube dashboard <ExternalLink size={13} />
              </Link>
            )}
            {canManage && (
              <HStack ml="auto">
                {status?.connected && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={Boolean(busy)}
                    onClick={() => void save('disconnect')}
                  >
                    Disconnect
                  </Button>
                )}
                <Button
                  size="sm"
                  loading={busy === 'connect'}
                  disabled={
                    Boolean(busy) ||
                    !status?.available ||
                    !config.channels.some(channel => channel.role === 'owned')
                  }
                  onClick={() => void save('connect')}
                >
                  {status?.connected ? 'Save changes' : 'Connect YouTube'}
                </Button>
              </HStack>
            )}
          </HStack>
        </VStack>
      )}
    </IntegrationCard>
  );
}
